import type { TFunction } from 'i18next'

// The scopes a merchant can grant, mirroring go-rever-lib's catalog. Left out on purpose: "*",
// which settings-api refuses; account, so a key cannot mint keys; analytics:write, which only
// covers what the dashboard itself sends; and logistics:retrigger, whose endpoint only REVER can call.
export const WILDCARD = '*'

// The public guide explaining what each scope lets a key do.
export const SCOPES_GUIDE_URL =
  'https://docs.itsrever.com/guides/api-key-scopes'

export interface ScopeGroup {
  id: string
  scopes: string[]
  // Third-party secrets: a leaked key would reach the merchant's platform, not just REVER.
  sensitive?: boolean
}

export const SCOPE_GROUPS: ScopeGroup[] = [
  {
    id: 'process',
    scopes: [
      'process:read',
      'process:create',
      'process:comment',
      'process:request_photos',
      'process:authorize',
      'process:review',
      'process:restock',
      'process:change_refund_method',
      'process:transactions',
      'process:mark_as_refunded',
      'process:update_status',
      'process:cancel',
      'process:update'
    ]
  },
  {
    id: 'logistics',
    scopes: [
      'logistics:read',
      'logistics:create',
      'logistics:update_shipping_status',
      'logistics:change_carrier',
      'logistics:set_store_address'
    ]
  },
  { id: 'analytics', scopes: ['analytics:read'] },
  { id: 'settings', scopes: ['settings:read', 'settings:write'] },
  {
    id: 'credentials',
    scopes: ['credentials:read', 'credentials:write'],
    sensitive: true
  }
]

// What "Select all" grants.
export const ALL_SCOPES = SCOPE_GROUPS.flatMap((group) => group.scopes)

// i18next reads ":" as a namespace separator, so "process:read" lives under scopes.process.read.
export const groupLabel = (group: ScopeGroup, t: TFunction) =>
  t(`api_keys.scopes.groups.${group.id}`)
export const scopeLabel = (scope: string, t: TFunction) =>
  t(`api_keys.scopes.${scope.replace(':', '.')}.label`)
export const scopeDescription = (scope: string, t: TFunction) =>
  t(`api_keys.scopes.${scope.replace(':', '.')}.description`)

// "Returns: Read, Add comments" for the list and the value dialog.
export function describeScopes(scopes: string[], t: TFunction): string[] {
  const named = SCOPE_GROUPS.flatMap((group) => {
    const held = group.scopes.filter((scope) => scopes.includes(scope))
    return held.length > 0
      ? [
          `${groupLabel(group, t)}: ${held.map((scope) => scopeLabel(scope, t)).join(', ')}`
        ]
      : []
  })
  // A key issued in the backoffice can hold scopes the picker never offers: show them as they are.
  const unlisted = scopes.filter(
    (scope) => scope !== WILDCARD && !ALL_SCOPES.includes(scope)
  )
  return unlisted.length > 0 ? [...named, unlisted.join(', ')] : named
}
